import { Trophy } from "lucide-react";
import { usePermissions, useStore, useTranslate } from "ra-core";
import { Link } from "react-router";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { DemoSampleDataButton } from "../demo/DemoSampleDataButton";
import { useReportsClient } from "../reports/ReportsClientContext";
import type { LeaderboardPeriod } from "../reports/types";
import { useReport } from "../reports/useReport";
import { leaderboardKey } from "./leaderboardKey";

const PERIODS: readonly LeaderboardPeriod[] = ["today", "week", "month"];
const METRICS = [
  "dials",
  "inbound",
  "conversations",
  "demos",
  "deals_won",
] as const;
const num = "text-right tabular-nums";

/**
 * "Classement" (X1): per-rep counts for today / this week / this month, visible to every active user. Data comes
 * from gavion_leaderboard (SECURITY DEFINER, counts and names only), so reps never read other reps' rows.
 * A rep name links to /rapports/vendeurs/:id (same period) for admins, and for the rep's own row; the detail RPC
 * re-checks it (gavion_rep_stats: admin or self).
 */
export const RankingCard = ({
  salesId,
  now,
  showLink = false,
  asPage = false,
}: {
  salesId: number;
  now: Date;
  /** Dashboard card: link to /rapports/classement. */
  showLink?: boolean;
  /** Report page: the title is the page h1. */
  asPage?: boolean;
}) => {
  const translate = useTranslate();
  const client = useReportsClient();
  const { permissions } = usePermissions();
  const isAdmin = permissions === "admin";
  const [stored, setPeriod] = useStore<LeaderboardPeriod>(
    "gavion.leaderboard.period",
    "today",
  );
  const period = PERIODS.includes(stored) ? stored : "today";
  const q = useReport(leaderboardKey(period, now), () =>
    client.leaderboard(period),
  );
  const t = (k: string) => translate(`gavion.goals.leaderboard.${k}`);
  const title = t("title");
  const Heading = asPage ? "h1" : "h2";

  const body = () => {
    if (q.isPending) return <Skeleton className="h-40 w-full rounded-lg" />;
    if (q.isError || !q.data)
      return (
        <p role="alert" className="text-sm text-destructive">
          {t("error")}
        </p>
      );
    const rows = q.data.rows;
    if (rows.length === 0)
      return (
        <>
          <p className="text-sm text-muted-foreground">{t("empty")}</p>
          {/* FakeRest demo only: sample reps for the rep stats pages */}
          <DemoSampleDataButton />
        </>
      );
    const leaderActive =
      rows[0] && METRICS.some((m) => (rows[0][m] ?? 0) > 0)
        ? rows[0].sales_id
        : null;
    return (
      <div className="min-w-0 overflow-x-auto">
        <Table aria-label={title}>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">{t("rank")}</TableHead>
              <TableHead>{t("rep")}</TableHead>
              {METRICS.map((m) => (
                <TableHead key={m} className="text-right">
                  {translate(`gavion.goals.metrics.${m}`)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => {
              const isMe = r.sales_id === salesId;
              return (
                <TableRow
                  key={r.sales_id}
                  aria-current={isMe ? "true" : undefined}
                  className={isMe ? "bg-primary/10 hover:bg-primary/15" : ""}
                >
                  <TableCell className="tabular-nums font-bold">
                    <span className="inline-flex items-center gap-1">
                      {i + 1}
                      {r.sales_id === leaderActive && (
                        <Trophy
                          role="img"
                          aria-label={t("leader")}
                          className="size-4 text-primary"
                        />
                      )}
                    </span>
                  </TableCell>
                  <TableCell className={isMe ? "font-bold" : "font-medium"}>
                    {isAdmin || isMe ? (
                      <Link
                        to={`/rapports/vendeurs/${r.sales_id}?period=${period}`}
                        title={translate("gavion.rep_stats.open_rep", {
                          name: r.name,
                        })}
                        className="inline-flex min-h-11 items-center text-accent-foreground underline underline-offset-4 outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-ring md:min-h-0"
                      >
                        {r.name}
                      </Link>
                    ) : (
                      r.name
                    )}
                    {isMe && ` (${t("you")})`}
                  </TableCell>
                  {METRICS.map((m) => (
                    <TableCell key={m} className={num}>
                      {r[m] ?? 0}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <section
      aria-labelledby="gavion-ranking"
      className="min-w-0 rounded-xl border bg-card p-4"
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <Heading
          id="gavion-ranking"
          className={
            asPage
              ? "flex items-center gap-2 text-2xl font-extrabold"
              : "flex items-center gap-2 text-lg font-bold"
          }
        >
          <Trophy className="size-5 text-primary" aria-hidden="true" />
          {title}
        </Heading>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={period}
          onValueChange={(v) => v && setPeriod(v as LeaderboardPeriod)}
          aria-label={t("period_label")}
          className="w-full sm:w-auto"
        >
          {PERIODS.map((p) => (
            <ToggleGroupItem
              key={p}
              value={p}
              className="flex-1 px-2 text-xs sm:flex-none sm:px-3 sm:text-sm"
            >
              {t(`periods.${p}`)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      {body()}
      {showLink && (
        <Link
          to="/rapports/classement"
          className="mt-3 inline-flex min-h-11 items-center text-sm font-semibold text-accent-foreground underline-offset-4 hover:underline md:min-h-0"
        >
          {t("see_all")}
        </Link>
      )}
    </section>
  );
};
